/**
 * Copyright (c) 2017 CRF Box Oy. All Rights Reserved. Company Confidential.
 */

import React, { useState, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { path } from 'ramda';
import Loading from '../loading/loading.component';
import { Grid, GridPanel } from 'tc-client-common';
import { showModal } from '../../actions/actions';
import { MODAL_TYPE_REMOVE_OBJECT_ID, MODAL_TYPE_UPDATE_OBJECT_ID } from '../../constants/modalTypes';
import RequestService from '../../service/requestService';
import getBiosamplesComponent from './biosamples-component';
import getDocumentBanner from './document-banner-component';
import dataReducer from './data-reducer';

import {
  RESPONSES_SIGNED_DOCUMENTS,
  // RESPONSES_EMPTY_SIGNED_DOCUMENTS,
  RESPONSES_DECLINED_DOCUMENTS,
  // RESPONSES_EMPTY_DECLINED_DOCUMENTS,
  RESPONSES_DATA_FETCH_ERROR,
  RESPONSES_QUESTION,
  RESPONSES_RESPONSE,
  RESPONSES_ITEM_ID,
  RESPONSES_REMOVE_BIOSAMPLE_CONFIRM,
  RESPONSES_UPDATE_BIOSAMPLE_CONFIRM,
  RESPONSES_CURRENT_BIOSAMPLE_CODE,
  RESPONSES_INFO_TOOLTIP_TEXT
} from '../../const';

import {
  SET_DATA,
  UPDATE_DOCUMENT,
  ADD_NEW_BIOSAMPLE_CODE,
  UPDATE_BIOSAMPLE_CODE,
  REMOVE_BIOSAMPLE_CODE,
  SIGNED_DOCUMENT,
  DECLINED_DOCUMENT
} from './constants';

// fetchData :: Number -> Promise
const fetchData = (participantId) => {
  return RequestService.getResponsesData(participantId);
};

// saveNewBiosampleCode :: hook reducer dispatch D => String -> (Object -> D) -> (Number, Object) -> Promise
const saveNewBiosampleCode = (studyGid) => (updateState) => (participantId, data) => {
  const { documentType, documentGid, documentVersion, questionId, biosampleCode } = data;

  return new Promise((resolve, reject) => {
    RequestService.saveNewBiosampleCode(participantId, {
      documentGid,
      documentVersion,
      questionId,
      biosampleCode,
      studyGid
    })
      .then((response) => {
        updateState({ newBiosampleCode: response.data, documentType, documentGid, questionId });
        resolve();
      })
      .catch((err) => {
        reject(err);
      });
  });
};

// updateBiosampleCode :: hook reducer dispatch D => Object -> (Object -> D) -> (Number, Object) -> Object -> Object -> Promise
const updateBiosampleCode =
  ({ studyGid, consentingGid }) =>
  (updateState) =>
  (participantId, data) =>
  ({ id }) =>
  ({ reason, biosampleCode }) => {
    const { documentType, documentGid, documentVersion, questionId } = data;

    return new Promise((resolve, reject) => {
      RequestService.updateBiosampleCode(participantId, {
        documentGid,
        documentVersion,
        questionId,
        biosampleId: id,
        reason,
        biosampleCode,
        studyGid,
        consentingGid
      })
        .then((response) => {
          updateState({ updatedBiosampleCode: response.data, documentType, documentGid, questionId });
          resolve();
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

// deleteItemId :: hook reducer dispatch D => (Object -> D) -> (Number, Object) -> Object -> Object -> Promise
const deleteItemId =
  ({ studyGid, consentingGid }) =>
  (updateState) =>
  (participantId, data) =>
  ({ id }) =>
  ({ reason }) => {
    const { documentType, documentGid, documentVersion, questionId } = data;

    return new Promise((resolve, reject) => {
      RequestService.removeBiosampleCode(participantId, {
        documentGid,
        documentVersion,
        questionId,
        biosampleId: id,
        reason,
        studyGid,
        consentingGid
      })
        .then(() => {
          updateState({ removedBiosampleId: id, documentType, documentGid, questionId, reason });
          resolve();
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

const changeDocumentVersion = (updateState) => (participantId, data) => (documentVersion) => {
  const { documentType, documentGid } = data;

  return new Promise((resolve, reject) => {
    RequestService.getDocumentVersion(participantId, { documentType, documentGid, documentVersion })
      .then((response) => {
        updateState({ document: response.data, documentType });
        resolve();
      })
      .catch(() => {
        reject();
      });
  });
};

const columnDefinition = [
  { label: RESPONSES_QUESTION, size: 50 },
  { label: RESPONSES_RESPONSE, size: 15 },
  {
    label: function ObjectIdHeader() {
      return (
        <div className="object-id-column-header">
          {RESPONSES_ITEM_ID}:
          <div className="object-id-tooltip">
            <i className="material-icons object-id-info">info</i>
            <div className="object-id-tooltip-content">{RESPONSES_INFO_TOOLTIP_TEXT}</div>
          </div>
        </div>
      );
    },
    size: 35
  }
];

export const Responses = ({
  studyGid,
  participantId,
  fetchData,
  saveNewBiosampleCode,
  showRemoveModal,
  showUpdateModal,
  changeDocumentVersion
}) => {
  const [state, dispatch] = useReducer(dataReducer, null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const addNewItemIdAction = (data) => dispatch({ type: ADD_NEW_BIOSAMPLE_CODE, payload: data });
  const updateItemIdAction = (data) => dispatch({ type: UPDATE_BIOSAMPLE_CODE, payload: data });
  const removedItemIdAction = (data) => dispatch({ type: REMOVE_BIOSAMPLE_CODE, payload: data });
  const changeDocumentVersionAction = (data) => dispatch({ type: UPDATE_DOCUMENT, payload: data });

  const getGridColumnsData = (
    { question, answer, biosamples, questionId },
    { documentGid, consentingGid, documentVersion, documentType, editable },
    permissions
  ) => [
    {
      columnData: question,
      id: RESPONSES_QUESTION
    },
    {
      columnData: answer,
      id: RESPONSES_RESPONSE
    },
    {
      columnData: getBiosamplesComponent({
        documentType,
        documentGid,
        documentVersion,
        questionId,
        biosamples,
        participantId,
        permissions,
        editable,
        addNewItemId: saveNewBiosampleCode(studyGid)(addNewItemIdAction),
        showRemoveModal: showRemoveModal({
          removeObjectId: deleteItemId({ studyGid, consentingGid })(removedItemIdAction)(participantId, {
            documentType,
            documentGid,
            documentVersion,
            questionId,
            biosamples
          })
        }),
        showUpdateModal: showUpdateModal({
          updateBiosampleCode: updateBiosampleCode({ studyGid, consentingGid })(updateItemIdAction)(participantId, {
            documentType,
            documentGid,
            documentVersion,
            questionId,
            biosamples
          })
        })
      }),
      id: RESPONSES_ITEM_ID
    }
  ];

  const getGridRowsData = (document, documentType, permissions) =>
    document.questions.map((question) => ({
      columns: getGridColumnsData(question, { ...document, documentType }, permissions),
      id: document.documentGid
    }));

  useEffect(() => {
    fetchData(participantId)
      .then((response) => {
        dispatch({ type: SET_DATA, payload: response.data });
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        setError(RESPONSES_DATA_FETCH_ERROR, err);
      });
  }, []);

  if (loading) return <Loading />;
  if (error) return <div className="responses-component-error">{error.message}</div>; // TODO: create common Error component and use it when ready

  const { signed, declined, permissions } = state || {};

  return (
    <section className="responses-component">
      {
        <GridPanel label={RESPONSES_SIGNED_DOCUMENTS}>
          {signed &&
            signed.map((document) => (
              <Grid
                key={document.documentGid + document.documentVersion}
                bannerLabel={getDocumentBanner({
                  participantId,
                  document,
                  documentType: SIGNED_DOCUMENT,
                  changeDocumentVersion: changeDocumentVersion(changeDocumentVersionAction)
                })}
                columnDefinition={columnDefinition}
                rows={getGridRowsData(document, SIGNED_DOCUMENT, permissions)}
                collapsable
              />
            ))}
        </GridPanel>
      }
      <br />
      {
        <GridPanel label={RESPONSES_DECLINED_DOCUMENTS}>
          {declined &&
            declined.map((document) => (
              <Grid
                key={document.documentGid + document.documentVersion}
                bannerLabel={getDocumentBanner({
                  participantId,
                  document,
                  documentType: DECLINED_DOCUMENT,
                  changeDocumentVersion: changeDocumentVersion(changeDocumentVersionAction)
                })}
                columnDefinition={columnDefinition}
                rows={getGridRowsData(document, DECLINED_DOCUMENT, permissions)}
                collapsable
              />
            ))}
        </GridPanel>
      }
    </section>
  );
};

Responses.propTypes = {
  participantId: PropTypes.number,
  studyGid: PropTypes.string,
  fetchData: PropTypes.func,
  saveNewBiosampleCode: PropTypes.func,
  showRemoveModal: PropTypes.func,
  showUpdateModal: PropTypes.func,
  changeDocumentVersion: PropTypes.func
};

const mapStateToProps = (store) => {
  const participant = path(['subject', 'participantJourney'], store) || {};

  return {
    participantId: participant.userId,
    studyGid: participant.studyGid
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchData,
    saveNewBiosampleCode,
    changeDocumentVersion,
    showRemoveModal:
      ({ removeObjectId }) =>
      ({ id, code }) =>
      () => {
        dispatch(
          showModal(MODAL_TYPE_REMOVE_OBJECT_ID, {
            shouldCloseOnOverlayClick: false,
            title: RESPONSES_REMOVE_BIOSAMPLE_CONFIRM,
            details: `${RESPONSES_ITEM_ID}: ${code}`,
            className: 'remove-biosample-code-modal',
            onSave: removeObjectId({ id, code })
          })
        );
      },
    showUpdateModal:
      ({ updateBiosampleCode }) =>
      ({ id, code }) =>
      () => {
        dispatch(
          showModal(MODAL_TYPE_UPDATE_OBJECT_ID, {
            shouldCloseOnOverlayClick: false,
            title: RESPONSES_UPDATE_BIOSAMPLE_CONFIRM,
            details: `${RESPONSES_CURRENT_BIOSAMPLE_CODE}: ${code}`,
            className: 'update-biosample-code-modal',
            onSave: updateBiosampleCode({ id, code })
          })
        );
      }
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Responses);
